import React, { useCallback, useRef, useState, useEffect } from "react";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import CoreIconButton from "../inputs/CoreIconButton";
import { Icon } from "@mui/material";
import CoreInput from "../inputs/CoreInput";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function CorePdfViewer({
  fileUrl,
  highlight = null,
  setHighlight = null,
}) {
  const [numPages, setNumPages] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const [containerWidth, setContainerWidth] = useState(null);
  const [pageDimensions, setPageDimensions] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  const containerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const resizeObserver = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });
    resizeObserver.observe(node);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (highlight) {
      setPageNumber(highlight.page);
      setBoundingBox(highlight.box);
    } else {
      setBoundingBox(null);
    }
  }, [highlight]);

  const renderHighlight = () => {
    if (!boundingBox || !pageDimensions || !containerWidth) return null;

    const [y1, x1, y2, x2] = boundingBox;
    const { originalWidth, originalHeight } = pageDimensions;
    const scale = containerWidth / originalWidth;

    // bounding box coords are top-left origin (x1,y1 = top-left corner, x2,y2 = bottom-right)
    return (
      <div
        style={{
          position: "absolute",
          top: `${y1 / 10}%`,
          left: `${x1 / 10}%`,
          height: `${(y2 - y1) / 10}%`,
          width: `${(x2 - x1) / 10}%`,

          backgroundColor: "rgba(255, 220, 0, 0.35)",
          border: "2px solid rgba(255, 160, 0, 0.8)",
          borderRadius: 5,
          pointerEvents: "none",
        }}
      />
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        maxHeight: "100%",
        maxWidth: "100%",
        marginTop: 32,
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <Page
              pageNumber={pageNumber}
              width={containerWidth || undefined}
              onLoadSuccess={(page) => {
                setPageDimensions({
                  originalWidth: page.originalWidth,
                  originalHeight: page.originalHeight,
                });
              }}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
            {renderHighlight()}
          </div>
        </Document>
      </div>
      <p
        style={{
          textAlign: "center",
          marginTop: "10px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CoreIconButton
          onClick={() => {
            setPageNumber(pageNumber - 1);
            setHighlight(null);
          }}
          disabled={pageNumber <= 1}
        >
          <Icon>chevron_left</Icon>
        </CoreIconButton>
        Page{" "}
        <div style={{ width: 50, margin: "0 8px" }}>
          <CoreInput
            type="number"
            value={pageNumber}
            onChange={(e) => setPageNumber(Number(e.target.value))}
          />{" "}
        </div>
        of {numPages}
        <CoreIconButton
          onClick={() => {
            setPageNumber(pageNumber + 1);
            setHighlight(null);
          }}
          disabled={pageNumber >= numPages}
        >
          <Icon>chevron_right</Icon>
        </CoreIconButton>
      </p>
    </div>
  );
}
